import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type ClipboardEvent,
  type ChangeEvent,
  type DragEvent,
  type FormEvent,
  type KeyboardEvent,
} from "react";

import { useModalDismiss } from "../hooks/useModalDismiss";
import {
  api,
  type FeedbackCategory,
  type FeedbackCreate,
} from "../services/api";

interface FeedbackModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess?: () => void;
  defaultPageUrl?: string;
  relatedAutotaskTicketId?: string;
  relatedFormRequestId?: string;
  relatedKbArticleId?: string;
}

interface PendingAttachment {
  localId: string;
  file: File;
  previewUrl: string;
  uploadedUrl: string | null;
  uploading: boolean;
  error: string | null;
}

const CATEGORIES: { value: FeedbackCategory; label: string; blurb: string }[] = [
  { value: "bug", label: "Bug", blurb: "Something is broken or behaving wrongly" },
  { value: "feature", label: "Idea", blurb: "Suggest an improvement or new capability" },
  { value: "question", label: "Question", blurb: "Ask how something works" },
  { value: "other", label: "Other", blurb: "Anything else" },
];

const ALLOWED_MIME = ["image/png", "image/jpeg", "image/gif", "image/webp"];
const MAX_FILE_SIZE = 5 * 1024 * 1024;
const MAX_ATTACHMENTS = 5;
const MIN_SUBJECT = 5;
const MAX_SUBJECT = 255;
const MIN_DESC = 10;
const MAX_DESC = 10_000;

function collectDiagnostics(pageUrl: string | undefined): Record<string, unknown> {
  return {
    userAgent: navigator.userAgent,
    language: navigator.language,
    screenResolution: `${screen.width}x${screen.height}`,
    viewport: `${innerWidth}x${innerHeight}`,
    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    timestamp: new Date().toISOString(),
    url: pageUrl ?? location.href,
  };
}

export function FeedbackModal({
  isOpen,
  onClose,
  onSuccess,
  defaultPageUrl,
  relatedAutotaskTicketId,
  relatedFormRequestId,
  relatedKbArticleId,
}: FeedbackModalProps) {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [category, setCategory] = useState<FeedbackCategory>("bug");
  const [subject, setSubject] = useState("");
  const [description, setDescription] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [attachments, setAttachments] = useState<PendingAttachment[]>([]);
  const [dragging, setDragging] = useState(false);
  // Counts dragenter/dragleave so we don't flicker the drop state as the cursor
  // crosses child elements inside the drop zone.
  const dragDepth = useRef(0);
  // This modal is always mounted by its parent and toggled via isOpen, so the
  // two gates are complementary, not redundant: `enabled = isOpen` stops the
  // hook locking scroll / trapping focus while closed, and the `if (!isOpen)
  // return null` below keeps the modal DOM out of the tree while closed.
  const panelRef = useModalDismiss(onClose, isOpen);

  useEffect(() => {
    if (isOpen) {
      setCategory("bug");
      setSubject("");
      setDescription("");
      setSubmitting(false);
      setError(null);
      setSuccess(false);
      setAttachments([]);
      setDragging(false);
      dragDepth.current = 0;
    }
  }, [isOpen]);

  useEffect(() => {
    return () => {
      attachments.forEach((a) => URL.revokeObjectURL(a.previewUrl));
    };
  }, [attachments]);

  const processFiles = useCallback(
    async (files: File[]) => {
      const remaining = MAX_ATTACHMENTS - attachments.length;
      if (remaining <= 0) {
        setError(`Maximum ${MAX_ATTACHMENTS} attachments`);
        return;
      }
      const accepted: PendingAttachment[] = [];
      for (const f of files.slice(0, remaining)) {
        if (!ALLOWED_MIME.includes(f.type)) {
          setError(`"${f.name}" is not a supported image type (PNG, JPEG, GIF, WebP).`);
          return;
        }
        if (f.size > MAX_FILE_SIZE) {
          setError(`"${f.name}" exceeds the 5 MB size limit.`);
          return;
        }
        accepted.push({
          localId: crypto.randomUUID(),
          file: f,
          previewUrl: URL.createObjectURL(f),
          uploadedUrl: null,
          uploading: true,
          error: null,
        });
      }
      setError(null);
      setAttachments((prev) => [...prev, ...accepted]);

      for (const a of accepted) {
        try {
          const res = await api.feedback.uploadAttachment(a.file);
          setAttachments((prev) =>
            prev.map((p) =>
              p.localId === a.localId
                ? { ...p, uploadedUrl: res.url, uploading: false }
                : p,
            ),
          );
        } catch (err) {
          const msg = err instanceof Error ? err.message : "upload failed";
          setAttachments((prev) =>
            prev.map((p) =>
              p.localId === a.localId
                ? { ...p, uploading: false, error: msg }
                : p,
            ),
          );
        }
      }
    },
    [attachments.length],
  );

  const handlePaste = useCallback(
    (e: ClipboardEvent) => {
      const items = e.clipboardData?.items;
      if (!items) return;
      const files: File[] = [];
      for (let i = 0; i < items.length; i++) {
        const it = items[i];
        if (it.kind === "file" && ALLOWED_MIME.includes(it.type)) {
          const f = it.getAsFile();
          if (f) files.push(f);
        }
      }
      if (files.length > 0) {
        e.preventDefault();
        void processFiles(files);
      }
    },
    [processFiles],
  );

  const handleFilePick = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      const files = e.target.files;
      if (files && files.length > 0) {
        void processFiles(Array.from(files));
      }
      if (fileInputRef.current) fileInputRef.current.value = "";
    },
    [processFiles],
  );

  const handleDragEnter = useCallback((e: DragEvent) => {
    if (!Array.from(e.dataTransfer.types).includes("Files")) return;
    e.preventDefault();
    dragDepth.current += 1;
    setDragging(true);
  }, []);

  const handleDragOver = useCallback((e: DragEvent) => {
    if (!Array.from(e.dataTransfer.types).includes("Files")) return;
    // Required for the drop event to fire.
    e.preventDefault();
  }, []);

  const handleDragLeave = useCallback((e: DragEvent) => {
    if (!Array.from(e.dataTransfer.types).includes("Files")) return;
    e.preventDefault();
    dragDepth.current = Math.max(0, dragDepth.current - 1);
    if (dragDepth.current === 0) setDragging(false);
  }, []);

  const handleDrop = useCallback(
    (e: DragEvent) => {
      if (!Array.from(e.dataTransfer.types).includes("Files")) return;
      e.preventDefault();
      dragDepth.current = 0;
      setDragging(false);
      const files = Array.from(e.dataTransfer.files);
      if (files.length > 0) void processFiles(files);
    },
    [processFiles],
  );

  function onCategoryKeyDown(e: KeyboardEvent<HTMLInputElement>) {
    const forward = e.key === "ArrowRight" || e.key === "ArrowDown";
    const back = e.key === "ArrowLeft" || e.key === "ArrowUp";
    if (!forward && !back) return;
    e.preventDefault();
    const index = CATEGORIES.findIndex((opt) => opt.value === category);
    const delta = forward ? 1 : -1;
    const next = CATEGORIES[(index + delta + CATEGORIES.length) % CATEGORIES.length];
    if (!next) return;
    setCategory(next.value);
    // Focus during the key event so the violet :focus-visible ring shows.
    document.getElementById(`feedback-category-${next.value}`)?.focus();
  }

  const removeAttachment = useCallback((localId: string) => {
    setAttachments((prev) => {
      const removed = prev.find((p) => p.localId === localId);
      if (removed) URL.revokeObjectURL(removed.previewUrl);
      return prev.filter((p) => p.localId !== localId);
    });
  }, []);

  const uploadsInFlight = attachments.some((a) => a.uploading);
  const hasFailedUploads = attachments.some((a) => a.error);
  // Failed screenshots are optional and are dropped from the payload below, so
  // they must NOT block submission — doing so silently disabled "Send feedback"
  // whenever an upload failed. Only an in-flight upload blocks (so we never
  // submit before a successful attachment's URL is known).
  const valid =
    subject.trim().length >= MIN_SUBJECT &&
    subject.length <= MAX_SUBJECT &&
    description.trim().length >= MIN_DESC &&
    description.length <= MAX_DESC &&
    !uploadsInFlight;

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    if (!valid || submitting) return;
    setSubmitting(true);
    setError(null);
    try {
      const attachment_urls = attachments
        .filter((a) => a.uploadedUrl && !a.error)
        .map((a) => a.uploadedUrl!);
      const payload: FeedbackCreate = {
        category,
        subject: subject.trim(),
        description: description.trim(),
        diagnostics: collectDiagnostics(defaultPageUrl),
        page_url: defaultPageUrl ?? location.href,
        related_autotask_ticket_id: relatedAutotaskTicketId,
        related_form_request_id: relatedFormRequestId,
        related_kb_article_id: relatedKbArticleId,
        attachment_urls: attachment_urls.length > 0 ? attachment_urls : undefined,
      };
      await api.feedback.create(payload);
      setSuccess(true);
      onSuccess?.();
      setTimeout(() => onClose(), 1500);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to submit feedback");
    } finally {
      setSubmitting(false);
    }
  }

  if (!isOpen) return null;

  return (
    <div className="modal-backdrop" onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div className="modal-panel modal-panel-md" ref={panelRef}>
        <header className="modal-header">
          <h2 className="modal-title">Send feedback</h2>
          <button
            type="button"
            className="modal-close"
            onClick={onClose}
            disabled={submitting}
            aria-label="Close"
          >
            ×
          </button>
        </header>
        <form className="modal-form" onSubmit={handleSubmit}>
          <div
            className={`modal-body${dragging ? " modal-body-dragging" : ""}`}
            onPaste={handlePaste}
            onDragEnter={handleDragEnter}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
          >
            <fieldset className="form-fieldset" disabled={submitting || success}>
              <legend className="form-legend">Category</legend>
              <div className="category-grid">
                {CATEGORIES.map((opt) => {
                  const selected = category === opt.value;
                  return (
                    <label
                      key={opt.value}
                      className={`category-option${selected ? " active" : ""}`}
                    >
                      <input
                        id={`feedback-category-${opt.value}`}
                        type="radio"
                        name="category"
                        value={opt.value}
                        checked={selected}
                        tabIndex={selected ? 0 : -1}
                        onChange={() => setCategory(opt.value)}
                        onKeyDown={onCategoryKeyDown}
                      />
                      <span className="category-label">{opt.label}</span>
                      <span className="category-blurb">{opt.blurb}</span>
                    </label>
                  );
                })}
              </div>
            </fieldset>

            <div className="form-field">
              <label htmlFor="feedback-subject">Subject</label>
              <input
                id="feedback-subject"
                type="text"
                value={subject}
                onChange={(e) => setSubject(e.target.value)}
                maxLength={MAX_SUBJECT}
                placeholder="One-line summary"
                disabled={submitting || success}
                required
              />
              <div className="form-field-meta">
                {subject.trim().length > 0 && subject.trim().length < MIN_SUBJECT && (
                  <span className="form-warning">At least {MIN_SUBJECT} characters</span>
                )}
                <span className="form-counter">
                  {subject.length} / {MAX_SUBJECT}
                </span>
              </div>
            </div>

            <div className="form-field">
              <label htmlFor="feedback-description">Description</label>
              <textarea
                id="feedback-description"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                maxLength={MAX_DESC}
                placeholder={
                  category === "bug"
                    ? "What happened? What did you expect? Steps to reproduce if you have them."
                    : "Tell us more."
                }
                rows={6}
                disabled={submitting || success}
                required
              />
              <div className="form-field-meta">
                {description.length > 0 && description.length < MIN_DESC && (
                  <span className="form-warning">At least {MIN_DESC} characters</span>
                )}
                <span className="form-counter">
                  {description.length.toLocaleString()} / {MAX_DESC.toLocaleString()}
                </span>
              </div>
            </div>

            <div className="form-field">
              <label>
                Screenshots{" "}
                <span className="form-optional">(optional, drag, paste, or click to add)</span>
              </label>
              {attachments.length > 0 && (
                <div className="attachment-grid">
                  {attachments.map((a) => (
                    <div key={a.localId} className="attachment-thumb">
                      <img src={a.previewUrl} alt={a.file.name} />
                      {a.uploading && <div className="attachment-overlay">Uploading…</div>}
                      {a.error && (
                        <div className="attachment-overlay attachment-overlay-error">
                          Failed
                        </div>
                      )}
                      <button
                        type="button"
                        className="attachment-remove"
                        onClick={() => removeAttachment(a.localId)}
                        aria-label={`Remove ${a.file.name}`}
                      >
                        ×
                      </button>
                    </div>
                  ))}
                </div>
              )}
              <input
                ref={fileInputRef}
                type="file"
                accept={ALLOWED_MIME.join(",")}
                multiple
                onChange={handleFilePick}
                style={{ display: "none" }}
              />
              <button
                type="button"
                className="attachment-add-btn"
                onClick={() => fileInputRef.current?.click()}
                disabled={attachments.length >= MAX_ATTACHMENTS || submitting || success}
              >
                Add screenshots
                <span className="attachment-help">
                  {attachments.length}/{MAX_ATTACHMENTS} · drag &amp; drop, paste, or click · PNG,
                  JPEG, GIF, WebP, max 5 MB each
                </span>
              </button>
              {hasFailedUploads && (
                <p className="form-warning" style={{ marginTop: 8 }}>
                  Some screenshots failed to upload and won&apos;t be included. You can still send
                  your feedback, or remove and re-add them.
                </p>
              )}
            </div>

            {error && <div className="form-error">{error}</div>}
            {success && (
              <div className="form-success">
                Thanks. We&apos;ve received your feedback and the iQuest team has been notified.
              </div>
            )}
          </div>
          <footer className="modal-footer">
            <button
              type="button"
              className="btn btn-ghost"
              onClick={onClose}
              disabled={submitting}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="btn btn-primary"
              disabled={!valid || submitting || success}
            >
              {submitting ? "Sending…" : success ? "Sent" : "Send feedback"}
            </button>
          </footer>
        </form>
      </div>
    </div>
  );
}
